// Bulk "Text Unpaid" composer for the active customer tab.
//
// "Unpaid" is computed locally as: status === 'active' AND no Stripe
// payment method on file (proxy for "hasn't paid this month / isn't
// auto-pay set up"). Mark sees a checkbox list before confirm so he can
// deselect anyone the proxy got wrong.
//
// Send fires one /sendSmsGatewayBulk call with the selected customerIds and the
// editable template. Tokens [Name] / [amount] / [link] are substituted
// per-customer server-side.

import { useMemo, useState } from 'react'
import { sendSmsBulk } from '../lib/stripe.js'

const DEFAULT_TEMPLATE =
  'Hi [Name], this is YesNet WiFi. Your invoice of $[amount] is due. ' +
  'Pay here: [link].'

export default function BulkSmsModal({ tabLabel, candidates, onClose }) {
  // candidates: [{ id, name, phone, monthlyAmount, ... }]
  const initial = useMemo(() => {
    const set = new Set()
    for (const c of candidates) if (c.phone) set.add(c.id)
    return set
  }, [candidates])
  const [selected, setSelected] = useState(initial)
  const [message, setMessage]   = useState(DEFAULT_TEMPLATE)
  const [busy, setBusy]         = useState(false)
  const [flash, setFlash]       = useState(null)
  const [result, setResult]     = useState(null)

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const selectedList = candidates.filter((c) => selected.has(c.id))
  const noPhoneCount = candidates.filter((c) => !c.phone).length

  const onSend = async () => {
    if (busy) return
    if (selectedList.length === 0) {
      setFlash({ kind: 'err', msg: 'Select at least one customer.' })
      return
    }
    const ok = window.confirm(
      `Send to ${selectedList.length} customer${selectedList.length === 1 ? '' : 's'}?\n\n` +
      `This will fire ${selectedList.length} SMS${selectedList.length === 1 ? '' : 'es'} immediately.`,
    )
    if (!ok) return
    setBusy(true)
    setFlash(null)
    setResult(null)
    try {
      const ids = selectedList.map((c) => c.id)
      const r = await sendSmsBulk(ids, message)
      setResult(r)
      setFlash({
        kind: r.failed === 0 ? 'ok' : 'err',
        msg: `${r.succeeded} sent · ${r.failed} failed`,
      })
    } catch (e) {
      setFlash({ kind: 'err', msg: e.message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Backdrop onClose={busy ? () => {} : onClose}>
      <div className="card w-full max-w-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">
              Bulk SMS · {tabLabel}
            </div>
            <h2 className="text-lg font-extrabold mt-0.5">
              Text {selectedList.length} unpaid customer{selectedList.length === 1 ? '' : 's'}
            </h2>
            {noPhoneCount > 0 && (
              <div className="text-xs text-amber-700 mt-1">
                {noPhoneCount} candidate{noPhoneCount === 1 ? '' : 's'} skipped (no phone on file).
              </div>
            )}
          </div>
          <button onClick={onClose} disabled={busy} aria-label="Close" className="btn-ghost text-lg disabled:opacity-30">✕</button>
        </div>

        <div className="mt-4">
          <label className="label">Message template</label>
          <textarea
            className="input min-h-[110px] resize-y"
            value={message}
            onChange={(e) => setMessage(e.target.value)} />
          <p className="text-[11px] text-gray-500 mt-1">
            Tokens substituted server-side per customer: <code>[Name]</code> · <code>[amount]</code> · <code>[link]</code>
          </p>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <span className="label !mb-0">Recipients</span>
            <div className="flex gap-2 text-[11px]">
              <button
                onClick={() => setSelected(new Set(candidates.filter((c) => c.phone).map((c) => c.id)))}
                className="text-brand-blue-deep underline">Select all</button>
              <button
                onClick={() => setSelected(new Set())}
                className="text-brand-blue-deep underline">Clear</button>
            </div>
          </div>
          <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto divide-y divide-gray-100">
            {candidates.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                No unpaid customers in this tab.
              </div>
            ) : candidates.map((c) => {
              const checked = selected.has(c.id)
              const disabled = !c.phone
              return (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 p-2.5 text-sm ${disabled ? 'opacity-50' : 'cursor-pointer hover:bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(c.id)}
                    className="w-4 h-4" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{c.name || '—'}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {c.phone ? formatPhone(c.phone) : 'no phone'} · ${Number(c.monthlyAmount || 0).toFixed(0)} due
                    </div>
                  </div>
                </label>
              )
            })}
          </div>
        </div>

        {result?.results?.length > 0 && (
          <div className="mt-3 border border-gray-200 rounded-md max-h-40 overflow-y-auto divide-y divide-gray-100 text-xs">
            {result.results.map((r, i) => {
              const c = candidates.find((x) => x.id === r.customerId)
              return (
                <div key={i} className="flex items-center justify-between p-2">
                  <span className="font-bold truncate">{c?.name || r.customerId}</span>
                  <span className={r.ok ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>
                    {r.ok ? '✓ sent' : `✗ ${r.reason}`}
                  </span>
                </div>
              )
            })}
          </div>
        )}

        {flash && (
          <div className={`mt-3 rounded-md p-3 text-sm font-bold ${
            flash.kind === 'ok'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>{flash.msg}</div>
        )}

        <div className="flex gap-2 mt-4">
          <button onClick={onClose} disabled={busy} className="btn-secondary flex-1">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={onSend}
              disabled={busy || selectedList.length === 0}
              className="btn-primary flex-1 disabled:opacity-50">
              {busy ? 'Sending…' : `Send to ${selectedList.length}`}
            </button>
          )}
        </div>
      </div>
    </Backdrop>
  )
}

function Backdrop({ onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}>
      <div className="mt-12" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

function formatPhone(p) {
  const d = String(p || '').replace(/\D+/g, '')
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`
  if (d.length === 11 && d.startsWith('1')) return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`
  return p || ''
}
